import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
  Business as BusinessIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { SketchPicker } from 'react-color';
import ModernLayout from './common/ModernLayout';
import ModernCard from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import { useBranding } from './common/BrandingSystem';
import axiosInstance from './axiosConfig';

const ColorPickerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '& .sketch-picker': {
    boxShadow: theme.shadows[8],
    borderRadius: theme.shape.borderRadius,
  },
}));

const LogoPreview = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `3px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[4],
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[8],
  },
}));

const BrandingSettings = () => {
  const theme = useTheme();
  const { branding, updateBranding, loadBranding } = useBranding();
  
  const [formData, setFormData] = useState({
    companyName: '',
    projectName: '',
    logo: null,
  });
  
  const [themeColors, setThemeColors] = useState({
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    accentColor: '#f093fb',
  });
  
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (branding && !branding.loading) {
      setFormData({
        companyName: branding.companyName || '',
        projectName: branding.projectName || '',
        logo: null,
      });
      setThemeColors({
        primaryColor: branding.theme?.primaryColor || '#667eea',
        secondaryColor: branding.theme?.secondaryColor || '#764ba2',
        accentColor: branding.theme?.accentColor || '#f093fb',
      });
    }
  }, [branding]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setMessage({ type: 'error', text: 'Logo file size must be less than 5MB' });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select a valid image file' });
        return;
      }
      
      setFormData(prev => ({ ...prev, logo: file }));
      setMessage({ type: 'success', text: 'Logo uploaded successfully' });
    }
  };

  const handleColorChange = (colorType, color) => {
    setThemeColors(prev => ({ ...prev, [colorType]: color.hex }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const newBranding = {
        companyName: formData.companyName,
        projectName: formData.projectName,
        logo: formData.logo,
        theme: themeColors,
      };

      await updateBranding(newBranding);
      setMessage({ type: 'success', text: 'Branding updated successfully!' });
      
      // Reload branding to get updated data
      await loadBranding();
    } catch (error) {
      console.error('Error saving branding:', error);
      setMessage({ type: 'error', text: 'Error saving branding. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      companyName: 'Core City Group',
      projectName: '',
      logo: null,
    });
    setThemeColors({
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      accentColor: '#f093fb',
    });
    setMessage({ type: 'info', text: 'Branding reset to defaults' });
  };

  if (branding.loading) {
    return (
      <ModernLayout title="Branding Settings">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout title="Branding Settings">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Branding Settings
        </Typography>
        <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
          Customize your company branding and project appearance
        </Typography>
      </Box>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <ModernCard title="Company Information" icon={<BusinessIcon />}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Company Logo
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <LogoPreview
                  src={formData.logo ? URL.createObjectURL(formData.logo) : branding.logoUrl}
                  alt="Company Logo"
                >
                  {formData.companyName.charAt(0) || 'C'}
                </LogoPreview>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoUpload}
                  />
                  <label htmlFor="logo-upload">
                    <GlassButton component="span" startIcon={<UploadIcon />}>
                      Upload Logo
                    </GlassButton>
                  </label>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.text.secondary }}>
                    Recommended: 200x200px, PNG or JPG, max 5MB
                  </Typography>
                </Box>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Project Name"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              placeholder="Enter your project name"
            />
          </ModernCard>
        </Grid>

        {/* Theme Colors */}
        <Grid item xs={12} md={6}>
          <ModernCard title="Theme Colors" icon={<PaletteIcon />}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Customize your brand colors
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(themeColors).map(([colorType, color]) => (
                <Grid item xs={12} sm={4} key={colorType}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                      {colorType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Typography>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: color,
                        border: `3px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                        cursor: 'pointer',
                        mx: 'auto',
                        mb: 1,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                      onClick={() => setShowColorPicker(showColorPicker === colorType ? null : colorType)}
                    />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {color}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {showColorPicker && (
              <ColorPickerContainer sx={{ mt: 2 }}>
                <SketchPicker
                  color={themeColors[showColorPicker]}
                  onChange={(color) => handleColorChange(showColorPicker, color)}
                />
              </ColorPickerContainer>
            )}
          </ModernCard>
        </Grid>

        {/* Preview */}
        <Grid item xs={12}>
          <ModernCard title="Brand Preview" icon={<ImageIcon />}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: `linear-gradient(135deg, ${themeColors.primaryColor} 0%, ${themeColors.secondaryColor} 100%)`,
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {formData.companyName.charAt(0) || 'C'}
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formData.companyName || 'Core City Group'}
                  </Typography>
                  {formData.projectName && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {formData.projectName}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                This is how your branding will appear throughout the application
              </Typography>
            </Box>
          </ModernCard>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <GlassButton
              onClick={handleReset}
              startIcon={<RefreshIcon />}
              disabled={saving}
            >
              Reset to Defaults
            </GlassButton>
            <GradientButton
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </GradientButton>
          </Box>
        </Grid>
      </Grid>
    </ModernLayout>
  );
};

export default BrandingSettings; 